import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useSubscription, useApolloClient } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiSend, 
  FiArrowLeft, 
  FiMoreVertical,
  FiImage,
  FiFile,
  FiUser,
  FiUserMinus,
  FiUserX
} from 'react-icons/fi';
import {
  GET_MY_CHATROOMS,
  GET_CHATROOM_MESSAGES,
  SEND_MESSAGE,
  MARK_MESSAGES_AS_READ,
  MESSAGE_RECEIVED,
  REMOVE_FRIEND,
  BLOCK_USER
} from '../graphql/friends';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import CommonTopicsDropdown from '../components/CommonTopicsDropdown';

const Chat = () => {
  const { userId } = useParams(); // 聊天對象的使用者ID
  const navigate = useNavigate();
  const { user } = useAuth();
  const apolloClient = useApolloClient();
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [currentChatroom, setCurrentChatroom] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const messagesEndRef = useRef(null);

  // GraphQL queries
  const { data: chatroomsData, loading: chatroomsLoading } = useQuery(GET_MY_CHATROOMS, {
    skip: !user,
    errorPolicy: 'all'
  });

  const { 
    data: messagesData, 
    loading: messagesLoading,
    refetch: refetchMessages
  } = useQuery(GET_CHATROOM_MESSAGES, {
    variables: { 
      chatroomId: currentChatroom?.id,
      limit: 50,
      skip: 0
    },
    skip: !currentChatroom?.id,
    errorPolicy: 'all'
  });

  // GraphQL mutations
  const [sendMessage] = useMutation(SEND_MESSAGE);
  const [markMessagesAsRead] = useMutation(MARK_MESSAGES_AS_READ);
  const [removeFriend] = useMutation(REMOVE_FRIEND, {
    refetchQueries: ['GetMyChatrooms', 'GetMyFriends'],
    awaitRefetchQueries: true
  });
  const [blockUser] = useMutation(BLOCK_USER, {
    refetchQueries: ['GetMyChatrooms', 'GetMyFriends'],
    awaitRefetchQueries: true
  });

  // 訂閱新消息
  const { data: newMessageData } = useSubscription(MESSAGE_RECEIVED, {
    variables: { chatroomId: currentChatroom?.id },
    skip: !currentChatroom?.id,
    onSubscriptionData: (options) => {
      console.log('📨 Subscription data received:', options.subscriptionData);
    },
    onError: (error) => {
      console.error('❌ Subscription error:', error);
    }
  });

  // 查找聊天室
  useEffect(() => {
    if (chatroomsData?.myChatrooms?.chatrooms && userId && user) {
      // 查找與指定使用者的直接聊天室
      const chatroom = chatroomsData.myChatrooms.chatrooms.find(room => 
        room.type === 'DIRECT' && 
        room.participants.some(p => p.id === userId)
      );
      
      if (chatroom) {
        setCurrentChatroom(chatroom);
      } else {
        // 如果沒有找到聊天室，說明尚未成爲好友
        console.log('No chatroom found with user:', userId, '- 可能尚未成爲好友');
        setCurrentChatroom(null);
      }
    }
  }, [chatroomsData, userId, user]);

  // 收到新消息時處理
  useEffect(() => {
    if (newMessageData?.messageReceived && currentChatroom?.id) {
      const newMessage = newMessageData.messageReceived;
      console.log('🔔 收到新消息:', newMessage);
      
      // 檢查是否為當前聊天室的消息，且不是自己發送的
      if (newMessage.chatroomId === currentChatroom.id && newMessage.senderId?.id !== user?.id) {
        try {
          // 讀取當前緩存的消息數據
          const existingData = apolloClient.readQuery({
            query: GET_CHATROOM_MESSAGES,
            variables: {
              chatroomId: currentChatroom.id,
              limit: 50,
              skip: 0
            }
          });

          if (existingData?.chatroomMessages?.messages) {
            // 檢查消息是否已存在（避免重複）
            const messageExists = existingData.chatroomMessages.messages.some(
              msg => msg.id === newMessage.id
            );

            if (!messageExists) {
              // 將新消息添加到消息列表的開頭（因為消息是倒序排列的）
              const updatedMessages = [newMessage, ...existingData.chatroomMessages.messages];
              
              // 更新 Apollo Client 緩存
              apolloClient.writeQuery({
                query: GET_CHATROOM_MESSAGES,
                variables: {
                  chatroomId: currentChatroom.id,
                  limit: 50,
                  skip: 0
                },
                data: {
                  chatroomMessages: {
                    ...existingData.chatroomMessages,
                    messages: updatedMessages,
                    totalCount: existingData.chatroomMessages.totalCount + 1
                  }
                }
              });

              // 滾動到底部
              setTimeout(() => scrollToBottom(), 100);
            }
          }
        } catch (error) {
          console.error('更新消息緩存失敗:', error);
          // 如果緩存更新失敗，回退到重新查詢
          refetchMessages();
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    }
  }, [newMessageData, currentChatroom?.id, user?.id, apolloClient, refetchMessages]);

  // 滾動到底部
  useEffect(() => {
    scrollToBottom();
  }, [messagesData]);

  // 標記消息爲已讀
  useEffect(() => {
    if (currentChatroom?.id) {
      markMessagesAsRead({
        variables: { chatroomId: currentChatroom.id }
      }).catch(console.error);
    }
  }, [currentChatroom?.id, markMessagesAsRead]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 發送消息
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !currentChatroom?.id || sending) return;

    const messageContent = messageText.trim();
    setSending(true);
    setMessageText(''); // 立即清空輸入框

    try {
      const { data } = await sendMessage({
        variables: {
          input: {
            chatroomId: currentChatroom.id,
            content: messageContent,
            type: 'TEXT'
          }
        },
        // 使用樂觀更新，立即顯示消息
        optimisticResponse: {
          sendMessage: {
            success: true,
            message: '消息發送中...',
            messageData: {
              __typename: 'Message',
              id: `temp-${Date.now()}`, // 臨時 ID
              chatroomId: currentChatroom.id,
              senderId: {
                __typename: 'User',
                id: user.id,
                username: user.username
              },
              content: messageContent,
              type: 'TEXT',
              filePath: null,
              status: 'SENT',
              sentAt: new Date().toISOString(),
              readAt: null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            },
            errors: [] // 添加缺少的 errors 字段
          }
        },
        // 更新緩存
        update: (cache, { data }) => {
          if (data?.sendMessage?.success && data.sendMessage.messageData) {
            try {
              // 讀取當前緩存
              const existingData = cache.readQuery({
                query: GET_CHATROOM_MESSAGES,
                variables: {
                  chatroomId: currentChatroom.id,
                  limit: 50,
                  skip: 0
                }
              });

              if (existingData?.chatroomMessages?.messages) {
                // 將新消息添加到列表開頭
                const updatedMessages = [data.sendMessage.messageData, ...existingData.chatroomMessages.messages];
                
                // 更新緩存
                cache.writeQuery({
                  query: GET_CHATROOM_MESSAGES,
                  variables: {
                    chatroomId: currentChatroom.id,
                    limit: 50,
                    skip: 0
                  },
                  data: {
                    chatroomMessages: {
                      ...existingData.chatroomMessages,
                      messages: updatedMessages,
                      totalCount: existingData.chatroomMessages.totalCount + 1
                    }
                  }
                });
              }
            } catch (error) {
              console.error('更新發送消息緩存失敗:', error);
            }
          }
        }
      });

      if (!data.sendMessage.success) {
        // 如果發送失敗，恢復輸入框內容
        setMessageText(messageContent);
        alert(data.sendMessage.message || '發送失敗');
      } else {
        // 滾動到底部
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      console.error('發送消息失敗:', error);
      // 恢復輸入框內容
      setMessageText(messageContent);
      alert('發送失敗，請重試');
    } finally {
      setSending(false);
    }
  };

  // 格式化時間
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      const now = new Date();
      const diff = now - date;
      
      // 如果是今天，只顯示時間
      if (diff < 24 * 60 * 60 * 1000) {
        return date.toLocaleTimeString('zh-CN', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
      
      // 否則顯示相對時間
      return formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: zhCN 
      });
    } catch (error) {
      return '';
    }
  };

  // 獲取聊天對象信息
  const getChatPartner = () => {
    if (!currentChatroom) return null;
    return currentChatroom.participants.find(p => p.id !== user?.id);
  };

  // 處理移除好友
  const handleRemoveFriend = async () => {
    if (!chatPartner) return;
    if (!window.confirm(`確定要移除好友 ${chatPartner.username} 嗎？`)) {
      return;
    }
    
    try {
      const { data } = await removeFriend({
        variables: { targetUserId: chatPartner.id }
      });
      
      if (data.removeFriend.success) {
        navigate('/friends');
      } else {
        alert(data.removeFriend.message || '操作失敗');
      }
    } catch (error) {
      console.error('移除好友失敗:', error);
      alert('操作失敗，請重試');
    }
  };

  // 處理封鎖使用者
  const handleBlockUser = async () => {
    if (!chatPartner) return;
    if (!window.confirm(`確定要封鎖使用者 ${chatPartner.username} 嗎？`)) {
      return;
    }
    
    try {
      const { data } = await blockUser({
        variables: { targetUserId: chatPartner.id }
      });
      
      if (data.blockUser.success) {
        navigate('/friends');
      } else {
        alert(data.blockUser.message || '操作失敗');
      }
    } catch (error) {
      console.error('封鎖使用者失敗:', error);
      alert('操作失敗，請重試');
    }
  };

  const chatPartner = getChatPartner();
  const messages = (messagesData?.chatroomMessages?.messages || []).slice().reverse();

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-500">請先登錄以使用聊天功能</p>
        </div>
      </div>
    );
  }

  if (chatroomsLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 mt-2">加載中...</p>
        </div>
      </div>
    );
  }

  if (!currentChatroom) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-500">未找到聊天室或該使用者不是你的好友</p>
          <button
            onClick={() => navigate('/friends')}
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <FiArrowLeft className="w-4 h-4 mr-2" />
            返回好友列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto h-screen flex flex-col">
      {/* 聊天頭部 */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/friends')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          
          {chatPartner && (
            <>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                {chatPartner.username.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">{chatPartner.username}</h1>
              </div>
            </>
          )}
        </div>

        <div className="relative">
          <button 
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiMoreVertical className="w-5 h-5" />
          </button>
          
          {showActions && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 origin-top-right">
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowActions(false);
                    navigate(`/user/${chatPartner?.id}`);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <FiUser className="w-4 h-4 mr-2" />
                  查看資料
                </button>
                <button
                  onClick={() => {
                    setShowActions(false);
                    handleRemoveFriend();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <FiUserMinus className="w-4 h-4 mr-2" />
                  移除好友
                </button>
                <button
                  onClick={() => {
                    setShowActions(false);
                    handleBlockUser();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <FiUserX className="w-4 h-4 mr-2" />
                  封鎖
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 點擊外部關閉菜單 */}
      {showActions && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowActions(false)}
        />
      )}

      {/* 消息區域 */}
      <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4">
        {messagesLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 mt-2 text-sm">加載消息中...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">還沒有消息，開始聊天吧！</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const isMyMessage = message.senderId.id === user.id;
              
              return (
                <div
                  key={message.id}
                  className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-xs lg:max-w-md ${isMyMessage ? 'order-2' : 'order-1'}`}>
                    <div
                      className={`px-4 py-2 rounded-lg border ${
                        isMyMessage
                          ? 'bg-purple-50 text-purple-900 border-purple-200'
                          : 'bg-yellow-50 text-gray-900 border-yellow-200'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                    </div>
                    <p className={`text-xs text-gray-500 mt-1 ${isMyMessage ? 'text-right' : 'text-left'}`}>
                      {formatTime(message.sentAt)}
                    </p>
                  </div>
                  
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 輸入區域 */}
      <div className="bg-white border-t border-gray-200 px-4 py-4 sticky bottom-0 left-0 right-0 z-10">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          {/* 共同話題按鈕 */}
          {chatPartner && (
            <CommonTopicsDropdown
              chatroomId={currentChatroom.id}
              targetUserId={chatPartner.id}
              targetUsername={chatPartner.username}
            />
          )}

          {/* 消息輸入 */}
          <div className="flex-1">
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="輸入訊息..."
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:ring-blue-500 focus:border-blue-500"
              disabled={sending}
            />
          </div>

          {/* 發送按鈕 */}
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            className="p-2 text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors"
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiSend className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat; 